import { useMemo, useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCasePayments from '../../../../hooks/useLegalCasePayments';
import useCurrentClaim from '../../../../hooks/useCurrentClaim';
import useLegalCaseParties from '../../../../hooks/useLegalCaseParties';
import type { Claim, PaidBySourceBucket, TitlePayment } from '../../../../types/LegalCase';
import LegalCasePaymentForm from './LegalCasePaymentForm';
import ConfirmDialog from './ConfirmDialog';
import { SectionHelpTitle } from './SectionHelp';

interface LegalCasePaymentsProps {
    caseId: string;
    // O claim dá os títulos para ancorar o lançamento manual e as partes para atribuir o pagador.
    claim?: Claim | null;
    initialPayments?: TitlePayment[];
}

// Selo da procedência: cobrança (a fonte externa), manual (o jurídico digitou) e depósito
// (abatimento de levantamento). A cor separa "veio de fora" de "lançamos aqui".
const sourceClass = (source: string): string => {
    if (source === 'SETTLEMENT_EVENT') return 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300';
    if (source === 'JUDICIAL_DEPOSIT') return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
    return 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200';
};

// Estorno é negativo e aparece em vermelho; pagamento em verde. O valor já vem assinado do backend.
const amountClass = (amount: number | null): string =>
    (amount ?? 0) < 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-800 dark:text-gray-100';

// Só o lançamento MANUAL é removível pela tela: o de evento é fato da cobrança e o de depósito
// tem ciclo preso ao levantamento (some quando o depósito é editado/removido).
const isRemovable = (payment: TitlePayment): boolean => payment.source === 'MANUAL';

// Quem pagou, na linha do lançamento. O nome pode ser nulo mesmo havendo parte escolhida: parte
// ligada ao cadastro `person` não guarda nome local (regra anti-duplicação), e aí o papel é a
// informação disponível — "pago pelo avalista" já responde o que o jurídico quer saber.
const payerLabel = (payment: TitlePayment): string => {
    if (payment.payerName) {
        return payment.isGuarantorPayment
            ? ` • pago pelo avalista ${payment.payerName}`
            : ` • pago por ${payment.payerName}`;
    }
    if (payment.payerRoleFormatted) return ` • pago pel${payment.isGuarantorPayment ? 'o avalista' : `a parte (${payment.payerRoleFormatted})`}`;
    return '';
};

// Texto do vínculo do lançamento: o nome do título quando atribuído (os títulos têm nome, é o que
// o analista reconhece), a operação quando é lançamento de operação (aval/depósito), e o genérico
// só quando o título não está mais na pretensão (removido depois do pagamento).
const attributionLabel = (payment: TitlePayment, titleNumber: string | null): string => {
    if (payment.unattributed) {
        return `Sem atribuição a título${payment.externalReference ? ` • operação ${payment.externalReference}` : ''}`;
    }
    return titleNumber ? `Título ${titleNumber}` : 'Atribuído a um título';
};

// AF-3995: o botão só aparece quando o backend diz que o lançamento manual é aceito (falso quando
// todos os títulos vêm da cobrança — ADR 005, decisão 7). O front não reimplementa a regra.
const canLaunchManualPayment = (claim: Claim | null): boolean => claim?.canAddManualPayment === true;

// Totais por origem no topo do extrato (AF-3995). Os números vêm prontos do backend
// (`claim.paidBySource`, lista fixa de três baldes); a tela só mostra, não soma.
const PaidBySourceTotals = ({ buckets }: { buckets: PaidBySourceBucket[] }) => (
    <section
        aria-label="Totais por origem"
        className="grid grid-cols-1 sm:grid-cols-3 gap-2"
    >
        {buckets.map((item) => (
            <div
                key={item.source}
                role="group"
                aria-label={item.sourceFormatted}
                className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2"
            >
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${sourceClass(item.source)}`}>
                    {item.sourceFormatted}
                </span>
                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {item.amountFormatted}
                </p>
            </div>
        ))}
    </section>
);

const PaymentItem = ({
    payment,
    titleNumber,
    onRemove,
    disabled,
}: {
    payment: TitlePayment;
    titleNumber: string | null;
    onRemove: () => void;
    disabled: boolean;
}) => (
    <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm font-medium ${amountClass(payment.amount)}`}>
                    {payment.amountFormatted}
                </span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${sourceClass(payment.source)}`}>
                    {payment.sourceFormatted}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {payment.movementTypeFormatted}
                </span>
                {/* Task 24: natureza vinda da cobrança. Confissão de dívida não é dinheiro — o selo
                    evita ler a linha como pagamento recebido. */}
                {payment.paymentNature && payment.paymentNature !== 'FINANCIAL' && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                        {payment.paymentNatureFormatted}
                    </span>
                )}
                {payment.occurredAt && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {formatDate(payment.occurredAt)}
                    </span>
                )}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                {attributionLabel(payment, titleNumber)}
                {payerLabel(payment)}
                {payment.paymentReferenceFormatted && ` • ${payment.paymentReferenceFormatted}`}
            </div>
            {payment.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">
                    {payment.description}
                </p>
            )}
        </div>
        {isRemovable(payment) && (
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                title="Remover lançamento"
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
            >
                <FaTrash size={11} />
            </button>
        )}
    </li>
);

// Seção "Pagamentos" do detalhe do caso (Task 18). Torna o ledger visível: cada lançamento com
// quanto, quando, a procedência (cobrança × manual × depósito) e o pagador. Permite o lançamento
// MANUAL (o VALOR AVAL da planilha). A remoção passa por ConfirmDialog (padrão do módulo).
const LegalCasePayments = ({ caseId, claim: initialClaim, initialPayments }: LegalCasePaymentsProps) => {
    const payments = useLegalCasePayments(caseId, initialPayments);
    // Releitura da pretensão depois de lançar ou remover: os totais por origem não ficam no retrato
    // de antes do lançamento (AF-3995).
    const [claimVersion, setClaimVersion] = useState(0);
    // Pretensão atual (não o retrato do page-view): título cadastrado nesta sessão aparece no seletor.
    const claim = useCurrentClaim(caseId, initialClaim, claimVersion);
    // Partes do caso: alimentam o seletor de pagador. `resolvedPeople` traz o nome de quem está no
    // cadastro `person` (a parte com `personId` não guarda nome local — regra anti-duplicação).
    const { parties, resolvedPeople } = useLegalCaseParties(caseId);
    const [showForm, setShowForm] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    // claimItemId → titleNumber: os títulos têm nome, e é ele que o analista reconhece. O claim já
    // está carregado (alimenta o formulário), então o vínculo do pagamento com o título se resolve
    // aqui, sem pedir o nome ao backend.
    const titleNumberById = useMemo(
        () => new Map((claim?.items ?? []).map((item) => [item.id, item.titleNumber])),
        [claim],
    );

    const openForm = () => {
        payments.clearErrors();
        setShowForm(true);
    };

    const closeForm = () => {
        payments.clearErrors();
        setShowForm(false);
    };

    const submit = async (value: Parameters<typeof payments.addPayment>[0]): Promise<boolean> => {
        const ok = await payments.addPayment(value);
        if (!ok) return false;
        closeForm();
        setClaimVersion((version) => version + 1);
        return true;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        const removed = await payments.removePayment(pendingRemoval);
        setPendingRemoval(null);
        if (removed) setClaimVersion((version) => version + 1);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <SectionHelpTitle title="Pagamentos" topic="payments.manual" />
                {!showForm && canLaunchManualPayment(claim) && (
                    <button
                        type="button"
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-600 dark:bg-gray-500 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Lançar pagamento
                    </button>
                )}
            </div>

            {claim?.paidBySource && <PaidBySourceTotals buckets={claim.paidBySource} />}

            {payments.payments.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum pagamento lançado</p>
            )}

            {payments.payments.length > 0 && (
                <ul>
                    {payments.payments.map((payment) => (
                        <PaymentItem
                            key={payment.id}
                            payment={payment}
                            titleNumber={payment.claimItemId ? titleNumberById.get(payment.claimItemId) ?? null : null}
                            onRemove={() => setPendingRemoval(payment.id)}
                            disabled={payments.isSaving || showForm}
                        />
                    ))}
                </ul>
            )}

            {showForm && (
                <LegalCasePaymentForm
                    claim={claim}
                    parties={parties}
                    resolvedPeople={resolvedPeople}
                    errorFields={payments.errorFields}
                    isSaving={payments.isSaving}
                    onSubmit={submit}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover lançamento"
                message="Remover este lançamento de pagamento? Esta ação não pode ser desfeita."
                isBusy={payments.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCasePayments;
